import React, { useState, useEffect, useRef, useCallback } from 'react';
import EditAllocationTable from './AllocatePOs/EditAllocationTable';
import {
  Dialog,
  DialogContent,
  Typography,
  Box,
  Button,
  TextField,
  FormControl,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
  Container,
  IconButton,
  makeStyles,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import PublishIcon from '@material-ui/icons/Publish';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { Autocomplete } from '@material-ui/lab';
import axios from 'axios';
import jwtDecode from 'jwt-decode';
import { appUrl } from '../../../../../utils/config';

const useStyles = makeStyles((theme) => ({
  dialogPaper: {
    borderRadius: 14,
    maxWidth: '90vw',
    position: 'relative',
  },
  dialogContent: {
    padding: 24,
    position: 'relative',
  },
  uploadingOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(255, 255, 255, 0.7)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(2),
    zIndex: 1300,
  },
  stepperHeader: {
    padding: '24px 0',
    textAlign: 'center',
    backgroundColor: '#F5F9FF',
    position: 'relative',
    marginBottom: theme.spacing(3),
  },
  heading: {
    fontWeight: 600,
    fontSize: 32,
    lineHeight: '100%',
    marginBottom: 12,
  },
  subheading: {
    fontWeight: 400,
    fontSize: 18,
    lineHeight: '100%',
    color: '#666',
  },
  closePageAction: {
    position: 'absolute',
    top: 12,
    right: 12,
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
    fontWeight: 'bold',
    color: '#000',
    '& svg': {
      marginLeft: 4,
      color: 'red',
    },
  },
  uploadBox: {
    width: '100%',
    marginTop: '1.5em',
    border: '1px solid black',
    backgroundColor: '#f5f5f5',
    padding: '2em 0',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    cursor: 'pointer',
    minHeight: 60,
  },
  uploadText: {
    marginLeft: 'auto',
    textAlign: 'center',
    flex: 1,
    fontSize: 16,
  },
  uploadIcon: {
    fontSize: '40px',
    marginLeft: 'auto',
    marginRight: '20px',
    color: '#333',
  },
  file: {
    marginTop: '1.5em',
    display: 'flex',
    flexDirection: 'column',
    gap: '1em',
    alignItems: 'flex-start',
  },
  link: {
    color: 'blue',
    textDecoration: 'underline',
  },
  linkBox: {
    cursor: 'pointer',
    display: 'flex',
    gap: '0.5em',
  },
  bold: {
    fontWeight: 500,
  },
  criteria: {
    maxWidth: '500px',
  },
  uploadBtn: {
    backgroundColor: '#ec6535',
    color: 'white',
    textTransform: 'none',
    borderRadius: '50px',
    margin: '1em auto',
    padding: '5px 30px',
    '&:hover': {
      backgroundColor: '#c6532b',
    },
  },
  ingestionTable: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(2),
    '& table': {
      minWidth: 1200,
    },
    '& td, & th': {
      borderRight: '1px solid #e0e0e0',
      fontSize: '16px',
    },
    '& td:last-child, & th:last-child': {
      borderRight: 'none',
    },
    '& .MuiTableCell-head': {
      backgroundColor: '#f9f9fc',
      fontWeight: 600,
      fontSize: 16,
      padding: '12px 8px',
    },
    '& .MuiTableCell-body': {
      padding: '8px',
    },
  },
  container: {
    textAlign: 'center',
    padding: '1rem',
  },
  totalRecordsBox: {
    border: '2px solid #707070',
    padding: '1rem',
  },
  validRecordsBox: {
    border: '2px solid #707070',
    padding: '1rem',
  },
  invalidRecordsBox: {
    border: '2px solid #707070',
    padding: '1rem',
  },
  number: {
    fontSize: '2.4rem',
    fontWeight: 500,
  },
  buttonContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: theme.spacing(3),
    width: '100%',
  },
  reUploadButton: {
    marginRight: 'auto',
    alignSelf: 'flex-start',
    textDecoration: 'underline',
    cursor: 'pointer',
    textTransform: 'none',
    color: '#000',
  },
  uploadButtonStyle: {
    backgroundColor: '#ec6535',
    color: '#fff',
    borderRadius: 20,
    padding: '6px 24px',
    textTransform: 'none',
    display: 'flex',
    alignItems: 'center',
    fontSize: '16px',
    gap: 8,
    whiteSpace: 'nowrap',
    '&:hover': {
      backgroundColor: '#c6532b',
    },
  },
}));

const getSteps = () => ['Upload file', 'Validation summary'];

// singlePOAllocation / poNumber — optional, passed ONLY by the Goods Receipt
// dashboard's Review Consumption screen (PA3ReviewConsumption.jsx): restricts
// the uploaded Excel's processing to that one batch's PO instead of every PO
// the file carries. The PO Collaboration dashboard's own "Allocate POs" usage
// (PurchaseOrderDashboard.jsx) passes neither, so it is unaffected.
// onUploadSuccess — optional; fired ONLY when the upload genuinely completed
// (EditAllocationTable's own saveBatchAllocationExcel call succeeded and its
// success popup was closed), never on a plain Cancel. ViewStructuredInformation
// uses this to chain into the same "Component allocation updated successfully"
// -> "Consumption details updated successfully" flow the pencil-edit path uses.
const AllocatePOsModal = ({ open, onClose, singlePOAllocation, poNumber, onUploadSuccess }) => {
  const classes = useStyles();
  const fileInputRef = useRef();
  const errorRef = useRef(null);

  const token = localStorage.getItem('authToken');
  const tokenData = token ? jwtDecode(token) : null;
  
  const [activeStep, setActiveStep] = useState(0);
  const [allocateFile, setAllocateFile] = useState(null);
  const [allocateFileType, setAllocateFileType] = useState('.xlsx');
  const [allocateIngestionSections, setAllocateIngestionSections] = useState([
    {
      id: 'ing_' + Date.now(),
      ingestionConfigId: 0,
      category: 'PO Collaboration',
      ingestThrough: '',
      fileHasMultipleTabs: 'Yes',
      tabs: [
        {
          id: 'ing_' + Date.now() + '_tab',
          tabName: '',
          headerRowNumber: '',
          columns: [
            {
              id: 'ing_' + Date.now() + '_col',
              columnName: '',
              fieldType: '',
              dataElement: '',
              dataElementId: null,
              dataElementLabel: '',
              ingestion: '',
              markForDelete: false,
              columnNameError: false,
              isMandatory: false,
            },
          ],
        },
      ],
    },
  ]);
  const [dataElements, setDataElements] = useState([]);
  const [isDataElementsLoading, setIsDataElementsLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadErrors, setUploadErrors] = useState([]);
  const [error, setError] = useState(null);
  const [validationSummary, setValidationSummary] = useState({
    totalRecords: 0,
    validRecords: 0,
    rejectedRecords: 0,
    rejectedRecordErrors: [],
  });
  const [validPOsFromUpload, setValidPOsFromUpload] = useState([]);
  const [editTableOpen, setEditTableOpen] = useState(false);

  const steps = getSteps();

  // Scroll to error message when error or uploadErrors are set
  useEffect(() => {
    if ((error || uploadErrors.length > 0) && errorRef.current) {
      errorRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [error, uploadErrors]);

  // Fetch data elements when modal opens
  useEffect(() => {
    const fetchDataElements = async () => {
      setIsDataElementsLoading(true);
      try {
        const response = await axios.get(
          `${appUrl}/${localStorage.appName}/${localStorage.network}/${localStorage.collabType}/${localStorage.roleName}/fetchDataElements`
        );
        if (response?.data?.status === 200 && response?.data?.data?.dataElementsList) {
          const groupedData = response.data.data.dataElementsList.reduce((acc, element) => {
            const existingCategory = acc.find((item) => item.category === element.category);
            if (existingCategory) {
              existingCategory.dataElements.push({
                dataElementId: element.dataElementId,
                dataElementKey: element.dataElementKey,
                dataElementLabel: element.dataElementLabel,
                markForDelete: element.markForDelete,
              });
            } else {
              acc.push({
                category: element.category,
                dataElements: [
                  {
                    dataElementId: element.dataElementId,
                    dataElementKey: element.dataElementKey,
                    dataElementLabel: element.dataElementLabel,
                    markForDelete: element.markForDelete,
                  },
                ],
              });
            }
            return acc;
          }, []);
          setDataElements(groupedData);

          // Fetch ingestion configurations after data elements are ready; keep the
          // loading flag up until this resolves too, so the table doesn't render
          // half-populated in between the two requests.
          await fetchIngestionConfigurationsWithDataElements(groupedData);
        }
      } catch (error) {
      } finally {
        setIsDataElementsLoading(false);
      }
    };

    if (open) {
      fetchDataElements();
    }
  }, [open]);

  const fetchIngestionConfigurationsWithDataElements = async (dataElementsList) => {
    if (!tokenData) return;
    
    try {
      const saasUserID = tokenData.saas_user_id;
      
      const response = await axios.get(
        `${appUrl}/${localStorage.appName}/${localStorage.network}/${localStorage.collabType}/${localStorage.roleName}/fetchIngestionConfigurations/0/${saasUserID}`
      );
      
      if (response?.data?.data?.ingestionConfigurations && response.data.data.ingestionConfigurations.length > 0) {
        // Find the most complete configuration (not empty)
        const completeConfigs = response.data.data.ingestionConfigurations.filter(config => 
          config.tabs && 
          config.tabs.length > 0 &&
          config.tabs[0].columns &&
          config.tabs[0].columns.length > 0 &&
          config.tabs[0].columns[0].columnName && 
          config.tabs[0].columns[0].columnName.trim() !== ''
        );

        const configToUse = completeConfigs.length > 0 ? completeConfigs[completeConfigs.length - 1] : response.data.data.ingestionConfigurations[0];

        // Transform backend format to frontend format
        const transformedSections = [{
          id: 'ing_' + Date.now(),
          ingestionConfigId: configToUse.ingestionConfigId || 0,
          category: configToUse.category || 'PO Batch Allocation',
          ingestThrough: configToUse.ingestThrough || 'File Upload',
          fileHasMultipleTabs: configToUse.fileHasMultipleTabs ? 'Yes' : 'No',
          tabs: configToUse.tabs.map((tab, tabIdx) => ({
            id: 'ing_' + Date.now() + '_tab_' + tabIdx,
            tabName: tab.tabName || '',
            headerRowNumber: configToUse.headerRowNumber ? String(configToUse.headerRowNumber) : '',
            columns: tab.columns.map((col, colIdx) => {
              // PO Number and BMS Material Batch Number always occupy the first two
              // column positions (they can never be deleted, and new columns are only
              // ever appended) so position alone identifies the mandatory columns.
              const isMandatoryPosition = colIdx === 0 || colIdx === 1;
              const mandatoryLabelForPosition = colIdx === 0 ? 'PO Number' : colIdx === 1 ? 'BMS Material Batch Number' : null;

              // Resolve the data element by ID first — IDs are stable within an
              // environment even when dataElementKey naming drifts across environments.
              // Only fall back to matching the legacy key string when the saved column
              // has no ID (older saved configs).
              let dataElementId = col.dataElementId || null;
              let dataElementLabel = '';

              if (dataElementId) {
                for (let category of dataElementsList) {
                  const found = category.dataElements.find(el => el.dataElementId === dataElementId);
                  if (found) {
                    dataElementLabel = found.dataElementLabel;
                    break;
                  }
                }
              } else if (col.dataElement) {
                for (let category of dataElementsList) {
                  const found = category.dataElements.find(el => el.dataElementKey === col.dataElement);
                  if (found) {
                    dataElementId = found.dataElementId;
                    dataElementLabel = found.dataElementLabel;
                    break;
                  }
                }
              }

              // Mandatory columns are never editable, so a resolution failure (saved
              // dataElement/dataElementId no longer matches, e.g. across environments)
              // can't be fixed by the user — look the label up directly instead of
              // leaving the cell blank.
              if (isMandatoryPosition && !dataElementLabel) {
                for (let category of dataElementsList) {
                  const found = category.dataElements.find(el => el.dataElementLabel === mandatoryLabelForPosition);
                  if (found) {
                    dataElementId = found.dataElementId;
                    dataElementLabel = found.dataElementLabel;
                    break;
                  }
                }
              }

              // If data element wasn't found but we have an ID/key, log a warning
              if ((col.dataElementId || col.dataElement) && !dataElementLabel) {
                console.warn(`Data element with id "${col.dataElementId}" / key "${col.dataElement}" not found in dataElementsList`);
              }

              // A saved config mapped to a now-withdrawn data element (Planned Material
              // Batch Number / Country of Origin) is cleared — the user must remap
              // that column before the file can be uploaded.
              const isWithdrawn = ["Planned Material Batch Number", "Country of Origin"].includes(dataElementLabel);

              const transformedCol = {
                id: 'ing_' + Date.now() + '_col_' + colIdx,
                columnName: col.columnName || '',
                fieldType: isWithdrawn ? '' : (col.fieldType || ''),
                dataElement: isWithdrawn ? '' : (col.dataElement || ''),
                dataElementId: isWithdrawn ? null : dataElementId,
                dataElementLabel: isWithdrawn ? '' : dataElementLabel,
                ingestion: col.ingestion || '',
                markForDelete: col.markForDelete || false,
                columnNameError: false,
                ingestionConfigColumnId: col.ingestionConfigColumnId,
                isMandatory: isMandatoryPosition,
              };

              return transformedCol;
            }),
          })),
        }];

        setAllocateIngestionSections(transformedSections);
      } else {
        // No ingestion configurations found - create default state with mandatory columns
        // Find data element IDs for mandatory columns
        let poNumberId = null;
        let poNumberLabel = '';
        let bmsMaterialBatchNumberId = null;
        let bmsMaterialBatchNumberLabel = '';
        
        for (let category of dataElementsList) {
          for (let element of category.dataElements) {
            if (element.dataElementLabel === 'PO Number') {
              poNumberId = element.dataElementId;
              poNumberLabel = element.dataElementLabel;
            }
            if (element.dataElementLabel === 'BMS Material Batch Number') {
              bmsMaterialBatchNumberId = element.dataElementId;
              bmsMaterialBatchNumberLabel = element.dataElementLabel;
            }
          }
        }
        
        // Create default state with 2 mandatory columns
        const defaultSections = [{
          id: 'ing_' + Date.now(),
          ingestionConfigId: 0,
          category: 'PO Batch Allocation',
          ingestThrough: 'File Upload',
          fileHasMultipleTabs: 'No',
          tabs: [
            {
              id: 'ing_' + Date.now() + '_tab',
              tabName: '',
              headerRowNumber: '1',
              columns: [
                {
                  id: 'ing_' + Date.now() + '_col_0',
                  columnName: '',
                  fieldType: 'Chain of custody',
                  dataElement: '',
                  dataElementId: poNumberId,
                  dataElementLabel: poNumberLabel,
                  ingestion: 'Mandatory',
                  markForDelete: false,
                  columnNameError: false,
                  ingestionConfigColumnId: 0,
                  isMandatory: true,
                },
                {
                  id: 'ing_' + Date.now() + '_col_1',
                  columnName: '',
                  fieldType: 'First Choice',
                  dataElement: '',
                  dataElementId: bmsMaterialBatchNumberId,
                  dataElementLabel: bmsMaterialBatchNumberLabel,
                  ingestion: 'Mandatory',
                  markForDelete: false,
                  columnNameError: false,
                  ingestionConfigColumnId: 0,
                  isMandatory: true,
                },
              ],
            },
          ],
        }];
        
        setAllocateIngestionSections(defaultSections);
      }
    } catch (error) {
    }
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile && (selectedFile.name.endsWith('.xlsx') || selectedFile.name.endsWith('.xls'))) {
      setAllocateFile(selectedFile);
      setUploadErrors([]);
      setError(null);
    } else {
      alert('Please upload a .xlsx or .xls file');
    }
    e.target.value = '';
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile && (droppedFile.name.endsWith('.xlsx') || droppedFile.name.endsWith('.xls'))) {
      setAllocateFile(droppedFile);
      setUploadErrors([]);
      setError(null);
    } else {
      alert('Please upload a .xlsx or .xls file');
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleRemoveFile = () => {
    if (isUploading) return;
    setAllocateFile(null);
    setUploadErrors([]);
    setError(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleUploadBoxClick = () => {
    if (!allocateFile || uploadErrors.length > 0 || error) {
      fileInputRef.current.click();
    }
  };

  const handleAddColumn = () => {
    const newSections = [...allocateIngestionSections];
    newSections[0].tabs[0].columns.push({
      id: 'ing_' + Date.now() + '_col_new',
      columnName: '',
      fieldType: '',
      dataElement: '',
      dataElementId: null,
      dataElementLabel: '',
      ingestion: '',
      markForDelete: false,
      columnNameError: false,
      isMandatory: false,
    });
    setAllocateIngestionSections(newSections);
  };

  const handleRemoveColumn = (tabIndex, colIndex) => {
    const newSections = [...allocateIngestionSections];
    const column = newSections[0].tabs[tabIndex].columns[colIndex];

    // Mandatory columns cannot be deleted
    if (column.isMandatory) {
      alert('This column is mandatory and cannot be deleted');
      return;
    }

    // If the column is an existing record (has an ingestionConfigColumnId — including 0,
    // a valid id, not "no id"), mark it for deletion so the backend receives the
    // instruction to remove it. Only a column with no id at all (never saved) can be
    // dropped locally with nothing lost.
    if (column.ingestionConfigColumnId !== null && column.ingestionConfigColumnId !== undefined) {
      // Existing record: mark for deletion to send to backend
      newSections[0].tabs[tabIndex].columns[colIndex].markForDelete = true;
    } else {
      // New local record: remove it completely
      newSections[0].tabs[tabIndex].columns.splice(colIndex, 1);
    }

    setAllocateIngestionSections(newSections);
  };

  const isChainOfCustodyUsed = (tab, currentColumnId) => {
    return tab.columns.some(
      (col) => col.id !== currentColumnId && col.fieldType === 'Chain of custody'
    );
  };

  // Planned Material Batch Number and Country of Origin are withdrawn from the
  // Allocate POs upload — they're maintained on the Batch Allocation tab instead.
  // Filtered client-side by label until the backend fetchDataElements response
  // stops returning them (or flags them markForDelete).
  const WITHDRAWN_DATA_ELEMENT_LABELS = [
    "Planned Material Batch Number",
    "Country of Origin",
  ];

  const getAvailableDataElements = (section, tab, column) => {
    // Get all data elements from all categories (don't filter by section category)
    // This allows prepopulated configs from backend to work correctly
    const allElements = (dataElements.flatMap(d => d.dataElements) || [])
      .filter((el) => !WITHDRAWN_DATA_ELEMENT_LABELS.includes(el.dataElementLabel));

    if (column.fieldType === 'Chain of custody') {
      const selectedByChainInSameTab = tab.columns
        .filter((col) => !col.markForDelete && col.id !== column.id && col.fieldType === 'Chain of custody')
        .map((col) => col.dataElementId)
        .filter(Boolean);
      return allElements
        .filter((el) => !el.markForDelete)
        .map((el) => ({
          value: el.dataElementId,
          label: el.dataElementLabel,
          dataElementKey: el.dataElementKey,
          dataElementId: el.dataElementId,
          disabled: selectedByChainInSameTab.includes(el.dataElementId),
        }));
    }

    if (column.fieldType === 'First Choice') {
      const selectedByFirstChoiceInSameTab = tab.columns
        .filter((col) => !col.markForDelete && col.id !== column.id && col.fieldType === 'First Choice')
        .map((col) => col.dataElementId)
        .filter(Boolean);
      const selectedByChainInSameTab = tab.columns
        .filter((col) => !col.markForDelete && col.fieldType === 'Chain of custody')
        .map((col) => col.dataElementId)
        .filter(Boolean);
      const selectedByFirstChoiceInOtherTabs = allocateIngestionSections
        .filter((s) => s.id !== section.id)
        .flatMap((s) => s.tabs)
        .filter((t) => t.id !== tab.id)
        .flatMap((t) => t.columns)
        .filter((col) => !col.markForDelete && col.fieldType === 'First Choice')
        .map((col) => col.dataElementId)
        .filter(Boolean);
      const disabledElements = [...selectedByFirstChoiceInSameTab, ...selectedByChainInSameTab, ...selectedByFirstChoiceInOtherTabs];
      return allElements
        .filter((el) => !el.markForDelete)
        .map((el) => ({
          value: el.dataElementId,
          label: el.dataElementLabel,
          dataElementKey: el.dataElementKey,
          dataElementId: el.dataElementId,
          disabled: disabledElements.includes(el.dataElementId),
        }));
    }

    if (column.fieldType === 'Second Choice') {
      const selectedByFirstChoice = allocateIngestionSections
        .flatMap((s) => s.tabs)
        .flatMap((t) => t.columns)
        .filter((col) => !col.markForDelete && col.fieldType === 'First Choice')
        .map((col) => col.dataElementId)
        .filter(Boolean);
      const selectedByChain = allocateIngestionSections
        .flatMap((s) => s.tabs)
        .flatMap((t) => t.columns)
        .filter((col) => !col.markForDelete && col.fieldType === 'Chain of custody')
        .map((col) => col.dataElementId)
        .filter(Boolean);
      const selectedBySecondChoiceInSameTab = tab.columns
        .filter((col) => !col.markForDelete && col.id !== column.id && col.fieldType === 'Second Choice')
        .map((col) => col.dataElementId)
        .filter(Boolean);
      const disabledElements = [...selectedByChain, ...selectedBySecondChoiceInSameTab];
      return allElements
        .filter((el) => !el.markForDelete)
        .filter((el) => selectedByFirstChoice.includes(el.dataElementId))
        .map((el) => ({
          value: el.dataElementId,
          label: el.dataElementLabel,
          dataElementKey: el.dataElementKey,
          dataElementId: el.dataElementId,
          disabled: disabledElements.includes(el.dataElementId),
        }));
    }

    return allElements
      .filter((el) => !el.markForDelete)
      .map((el) => ({
        value: el.dataElementId,
        label: el.dataElementLabel,
        dataElementKey: el.dataElementKey,
        dataElementId: el.dataElementId,
        disabled: false,
      }));
  };

  const validateIngestionConfig = () => {
    const section = allocateIngestionSections[0];
    if (!section || !section.tabs || section.tabs.length === 0) {
      setError('Please add ingestion configuration details');
      return false;
    }

    for (let tab of section.tabs) {
      // Validate Tab Name
      if (!tab.tabName || !tab.tabName.trim()) {
        setError('Tab Name is required');
        return false;
      }

      // Validate Header Row Number
      if (!tab.headerRowNumber || !tab.headerRowNumber.trim()) {
        setError('Header Row Number is required');
        return false;
      }

      if (isNaN(parseInt(tab.headerRowNumber)) || parseInt(tab.headerRowNumber) < 1) {
        setError('Header Row Number must be a positive number');
        return false;
      }

      // Validate columns
      const activeColumns = tab.columns.filter(col => !col.markForDelete);
      if (activeColumns.length === 0) {
        setError('At least one column is required');
        return false;
      }

      for (let col of activeColumns) {
        if (!col.columnName || !col.columnName.trim()) {
          setError('Column Name is required for all columns');
          return false;
        }

        if (!col.fieldType) {
          setError(`Field Type is required for column "${col.columnName}"`);
          return false;
        }

        if (!col.dataElementId) {
          setError(`Data Element is required for column "${col.columnName}"`);
          return false;
        }

        if (!col.ingestion) {
          setError(`Ingestion is required for column "${col.columnName}"`);
          return false;
        }
      }
    }

    // PO Number / BMS Material Batch Number are mandatory but not user-editable,
    // so a missing one isn't a fixable form error — the Upload button is disabled
    // instead of surfacing a message here (see areMandatoryDataElementsPresent).
    if (!areMandatoryDataElementsPresent()) {
      return false;
    }

    return true;
  };

  // PO Number and BMS Material Batch Number are mandatory data elements that are
  // never user-editable (see fetchIngestionConfigurationsWithDataElements). If
  // they can't be resolved for some reason, block upload silently rather than
  // showing an error the user has no way to act on.
  const areMandatoryDataElementsPresent = () => {
    const section = allocateIngestionSections[0];
    if (!section || !section.tabs || section.tabs.length === 0) return false;

    return section.tabs.every((tab) => {
      const activeLabels = tab.columns.filter((col) => !col.markForDelete).map((col) => col.dataElementLabel);
      return activeLabels.includes('PO Number') && activeLabels.includes('BMS Material Batch Number');
    });
  };

  // A Second Choice column only makes sense paired with a First Choice mapped to
  // the same data element (that's how getAvailableDataElements populates its
  // options) — but a column loaded from a saved config could have drifted out of
  // sync, so this is checked explicitly rather than trusted from the dropdown.
  const isSecondChoiceValid = () => {
    const firstChoiceIds = allocateIngestionSections
      .flatMap((s) => s.tabs)
      .flatMap((t) => t.columns)
      .filter((col) => !col.markForDelete && col.fieldType === 'First Choice')
      .map((col) => col.dataElementId)
      .filter(Boolean);

    return allocateIngestionSections
      .flatMap((s) => s.tabs)
      .flatMap((t) => t.columns)
      .filter((col) => !col.markForDelete && col.fieldType === 'Second Choice')
      .every((col) => col.dataElementId && firstChoiceIds.includes(col.dataElementId));
  };

  // Mirrors validateIngestionConfig's field-level checks, but as a boolean so the
  // Upload button can stay disabled while a row (e.g. one just added via "+ Add
  // column") is still incomplete, instead of only catching it after the click.
  const isIngestionConfigComplete = () => {
    const section = allocateIngestionSections[0];
    if (!section || !section.tabs || section.tabs.length === 0) return false;

    const tabsComplete = section.tabs.every((tab) => {
      if (!tab.tabName || !tab.tabName.trim()) return false;
      if (!tab.headerRowNumber || !tab.headerRowNumber.trim()) return false;

      const headerRowNumber = parseInt(tab.headerRowNumber, 10);
      if (isNaN(headerRowNumber) || headerRowNumber < 1) return false;

      const activeColumns = tab.columns.filter((col) => !col.markForDelete);
      if (activeColumns.length === 0) return false;

      return activeColumns.every(
        (col) => col.columnName && col.columnName.trim() && col.fieldType && col.dataElementId && col.ingestion
      );
    });

    return tabsComplete && areMandatoryDataElementsPresent() && isSecondChoiceValid();
  };

  const handleUpload = async () => {
    if (!allocateFile) {
      setError('Please select a file to upload');
      return;
    }

    // Validate ingestion configuration table
    if (!validateIngestionConfig()) {
      return;
    }

    if (allocateFile && !isUploading) {
      setIsUploading(true);
      setUploadErrors([]);
      setError(null);
      
      try {
        // Step 1: Update ingestion configuration
        const ingestionPayload = {
          ingestionConfigurations: allocateIngestionSections.map(section => ({
            category: 'PO Batch Allocation',
            ingestThrough: 'File Upload',
            fileHasMultipleTabs: false,
            headerRowNumber: parseInt(section.tabs[0]?.headerRowNumber) || null,
            saasUserID: tokenData?.saas_user_id,
            // vendorMosymphonyID: tokenData?.organization_id,
            brandOwnerMosymphonyID: tokenData?.organization_id,
            ingestionConfigId: section.ingestionConfigId || 0,
            columnMappings: section.tabs.flatMap(tab => 
              tab.columns.map(col => ({
                ingestionConfigColumnId: col.ingestionConfigColumnId || 0,
                tabName: tab.tabName,
                columnName: col.columnName,
                dataElementId: col.dataElementId,
                fieldType: col.fieldType,
                ingestion: col.ingestion,
                markForDelete: col.markForDelete || false,
              }))
            ),
          })),
        };

        // return

        const configResponse = await axios.post(
          `${appUrl}/${localStorage.appName}/${localStorage.network}/${localStorage.collabType}/${localStorage.roleName}/updateIngestionConfiguration`,
          ingestionPayload
        );

        // Step 2: Upload the file
        const formData = new FormData();
        formData.append('files', allocateFile);
        // Goods Receipt only (see prop comment above) — restricts processing to
        // this one PO instead of every PO the uploaded file carries. Sent as a
        // JSON-stringified `data` field, NOT separate form-data keys.
        if (singlePOAllocation) {
          formData.append('data', JSON.stringify({ singlePOAllocation: true, poNumber }));
        }

        const uploadResponse = await axios.post(
          `${appUrl}/${localStorage.appName}/${localStorage.network}/${localStorage.collabType}/${localStorage.roleName}/uploadBatchAllocationExcel`,
          formData,
          {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          }
        );
        
        // Check for API error status
        if (uploadResponse?.data?.status === false) {
          setIsUploading(false);
          setError(uploadResponse?.data?.errorMessage || 'An error occurred during file processing');
          return;
        }
        
        // Handle response with validPOsList and invalidPOsList
        if (uploadResponse?.data?.validPOsList !== undefined && uploadResponse?.data?.invalidPOsList !== undefined) {
          const validPOs = uploadResponse.data.validPOsList || [];
          const invalidPOs = uploadResponse.data.invalidPOsList || [];
          const totalRecords = validPOs.length + invalidPOs.length;
          
          // Map invalid POs to error format and sort by row number
          const invalidPOErrors = invalidPOs
            .map((po) => ({
              rowNumber: `Row ${po.excelRowNumber}`,
              message: po.errorMessage,
              po: po, // Keep full PO object for reference
              excelRowNumber: po.excelRowNumber, // Keep for sorting
            }))
            .sort((a, b) => a.excelRowNumber - b.excelRowNumber);
          
          setValidationSummary({
            totalRecords: totalRecords,
            validRecords: validPOs.length,
            rejectedRecords: invalidPOs.length,
            rejectedRecordErrors: invalidPOErrors,
          });
          setValidPOsFromUpload(validPOs);

          // Move to validation summary step
          setActiveStep(1);
          setIsUploading(false);
          return;
        }
        
        // Extract validation summary from response (legacy format)
        if (uploadResponse?.data?.data) {
          const responseData = uploadResponse.data.data;
          setValidationSummary({
            totalRecords: responseData.totalRecords || 0,
            validRecords: responseData.validRecords || 0,
            rejectedRecords: responseData.rejectedRecords || 0,
            rejectedRecordErrors: responseData.rejectedRecordErrors || [],
          });
        }
        
        // Move to validation summary step
        setActiveStep(1);
        setIsUploading(false);
      } catch (error) {
        setIsUploading(false);
        setUploadErrors([error.response?.data?.message || 'An error occurred while uploading. Please try again.']);
      }
    }
  };

  const handleClose = () => {
    // Reset state
    setActiveStep(0);
    setAllocateFile(null);
    setAllocateFileType('.xlsx');
    setAllocateIngestionSections([
      {
        id: 'ing_' + Date.now(),
        category: 'PO Collaboration',
        ingestThrough: '',
        fileHasMultipleTabs: 'Yes',
        tabs: [
          {
            id: 'ing_' + Date.now() + '_tab',
            tabName: '',
            headerRowNumber: '',
            columns: [
              {
                id: 'ing_' + Date.now() + '_col',
                columnName: '',
                fieldType: '',
                dataElement: '',
                dataElementId: null,
                dataElementLabel: '',
                ingestion: '',
                markForDelete: false,
                columnNameError: false,
                isMandatory: false,
              },
            ],
          },
        ],
      },
    ]);
    setIsUploading(false);
    setUploadErrors([]);
    setError(null);
    setValidPOsFromUpload([]);
    setEditTableOpen(false);
    onClose();
  };

  // Genuine save success (EditAllocationTable's saveBatchAllocationExcel call
  // succeeded) — same reset as a plain close, plus the extra signal for
  // callers that need to react specifically to a completed upload.
  const handleSaveSuccess = () => {
    handleClose();
    if (onUploadSuccess) onUploadSuccess();
  };

  const handleBack = () => setActiveStep((prev) => prev - 1);

  const getStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Container className={classes.container}>
            <Box ref={errorRef}>
              {error && (
                <Container
                  style={{
                    width: '100%',
                    border: '2px solid red',
                    backgroundColor: '#ffeaea',
                    padding: '0.8rem',
                    marginBottom: '1.5em',
                    textAlign: 'center',
                    fontSize: '0.875rem',
                  }}
                >
                  <Typography variant="body2">{error}</Typography>
                </Container>
              )}
              
              {uploadErrors.length > 0 && (
                <Box mt={2} style={{ width: '100%' }}>
                  {uploadErrors.map((err, idx) => (
                    <Container
                      key={idx}
                      style={{
                        width: '100%',
                        border: '2px solid red',
                        backgroundColor: '#ffeaea',
                        padding: '0.8rem',
                        marginBottom: '0.5em',
                        textAlign: 'center',
                        fontSize: '0.875rem',
                      }}
                    >
                      <Typography variant="body2">{err}</Typography>
                    </Container>
                  ))}
                </Box>
              )}
            </Box>

            <Box
              className={classes.uploadBox}
              onClick={handleUploadBoxClick}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              style={{
                cursor: !allocateFile || uploadErrors.length > 0 || error ? 'pointer' : 'not-allowed',
                opacity: allocateFile && !(uploadErrors.length > 0 || error) ? 0.6 : 1,
              }}
            >
              <input
                type="file"
                hidden
                ref={fileInputRef}
                onChange={handleFileChange}
                accept=".xlsx, .xls"
              />
              <Typography className={classes.uploadText}>Drag & drop file or click here to upload</Typography>
              <PublishIcon className={classes.uploadIcon} />
            </Box>

            <Box className={classes.file}>
              <Typography>Select file type:</Typography>
              <FormControl variant="outlined" size="small">
                <Select
                  value={allocateFileType}
                  onChange={(e) => setAllocateFileType(e.target.value)}
                  disabled={allocateFile !== null && !(uploadErrors.length > 0 || error)}
                >
                  <MenuItem value=".xlsx">.xlsx</MenuItem>
                  <MenuItem value=".xls">.xls</MenuItem>
                </Select>
              </FormControl>

              {allocateFile && (
              <Box className={classes.linkBox}>
                <Typography className={classes.link}>{allocateFile.name}</Typography>
                <span onClick={handleRemoveFile} style={{ cursor: 'pointer' }}>
                  ❌
                </span>
              </Box>
            )}

              <Box style={{textAlign: "left"}}>
                <Typography className={classes.bold}>Validation criteria</Typography>
                <Typography className={classes.criteria}>Only excel files accepted (.xls, or .xlsx)</Typography>
                {/* <Typography>First row must contain headers only</Typography> */}
                {/* <Typography>File should not contain blank rows or columns</Typography> */}
              </Box>
            </Box>

            {/* Ingestion Configuration Table */}
            <Typography style={{ textAlign: "left", fontWeight: 600, marginTop: '2em', marginBottom: '1em', fontSize: 16 }}>
              Ingestion Configuration
            </Typography>
            <TableContainer component={Paper} className={classes.ingestionTable}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell style={{ width: '150px', fontWeight: 600 }}>Tab Name <span style={{ color: 'red' }}>*</span></TableCell>
                    <TableCell style={{ width: '150px', fontWeight: 600 }}>Header Row Number <span style={{ color: 'red' }}>*</span></TableCell>
                    <TableCell style={{ width: '180px', fontWeight: 600 }}>Column Name <span style={{ color: 'red' }}>*</span></TableCell>
                    <TableCell style={{ width: '140px', fontWeight: 600 }}>Field Type <span style={{ color: 'red' }}>*</span></TableCell>
                    <TableCell style={{ width: '250px', fontWeight: 600 }}>Data Elements <span style={{ color: 'red' }}>*</span></TableCell>
                    <TableCell style={{ width: '130px', fontWeight: 600 }}>Ingestion <span style={{ color: 'red' }}>*</span></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {isDataElementsLoading ? (
                    <TableRow>
                      <TableCell colSpan={6} style={{ textAlign: 'center', padding: '3em' }}>
                        <CircularProgress size={28} />
                      </TableCell>
                    </TableRow>
                  ) : allocateIngestionSections[0]?.tabs.map((tab, tabIndex) => {
                    if (tab.columns.length === 0) {
                      return (
                        <TableRow key="empty-row">
                          <TableCell colSpan={6} style={{ textAlign: 'center', color: '#999', padding: '2em' }}>
                            No columns added. Click "+ Add column" to get started.
                          </TableCell>
                        </TableRow>
                      );
                    }
                    // Filter out columns marked for deletion from display
                    const displayColumns = tab.columns.filter(col => !col.markForDelete);
                    if (displayColumns.length === 0) {
                      return (
                        <TableRow key="empty-row">
                          <TableCell colSpan={6} style={{ textAlign: 'center', color: '#999', padding: '2em' }}>
                            No columns added. Click "+ Add column" to get started.
                          </TableCell>
                        </TableRow>
                      );
                    }
                    const visibleColumnCount = displayColumns.length;
                    return displayColumns.map((col, displayColIndex) => {
                      const actualColIndex = tab.columns.findIndex(c => c.id === col.id);
                      return (
                        <TableRow key={col.id}>
                          {displayColIndex === 0 && (
                            <>
                              <TableCell rowSpan={visibleColumnCount}>
                                <TextField
                                  size="small"
                                  variant="outlined"
                                  value={tab.tabName}
                                  onChange={(e) => {
                                    const newSections = [...allocateIngestionSections];
                                    newSections[0].tabs[tabIndex].tabName = e.target.value;
                                    setAllocateIngestionSections(newSections);
                                  }}
                                  placeholder="Enter tab name"
                                  fullWidth
                                />
                              </TableCell>
                              <TableCell rowSpan={visibleColumnCount}>
                                <TextField
                                  size="small"
                                  variant="outlined"
                                  type="text"
                                  value={tab.headerRowNumber}
                                  onKeyDown={(e) => {
                                    // Block anything that isn't a digit, Backspace, Delete, Arrow keys, Tab, Home, End
                                    const allowed = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab', 'Home', 'End'];
                                    if (!allowed.includes(e.key) && !/^\d$/.test(e.key)) {
                                      e.preventDefault();
                                      return;
                                    }
                                    // Block 0 when field is empty (prevents leading zero / standalone 0)
                                    if (e.key === '0' && !tab.headerRowNumber) {
                                      e.preventDefault();
                                    }
                                  }}
                                  onChange={(e) => {
                                    const newSections = [...allocateIngestionSections];
                                    // Strip anything not a digit
                                    const value = e.target.value.replace(/\D/g, '');
                                    newSections[0].tabs[tabIndex].headerRowNumber = value;
                                    setAllocateIngestionSections(newSections);
                                  }}
                                  onBlur={(e) => {
                                    // Trim and remove leading zeros; reset to '' if 0 or empty
                                    const newSections = [...allocateIngestionSections];
                                    const trimmed = e.target.value.trim().replace(/^0+/, '');
                                    newSections[0].tabs[tabIndex].headerRowNumber = trimmed || '';
                                    setAllocateIngestionSections(newSections);
                                  }}
                                  inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                                  placeholder="1"
                                  fullWidth
                                />
                              </TableCell>
                          </>
                        )}
                        <TableCell>
                          <div style={{ display: 'flex', alignItems: 'center' }}>
                            <TextField
                              size="small"
                              variant="outlined"
                              value={col.columnName}
                              onChange={(e) => {
                                const newSections = [...allocateIngestionSections];
                                newSections[0].tabs[tabIndex].columns[actualColIndex].columnName = e.target.value;
                                setAllocateIngestionSections(newSections);
                              }}
                              placeholder="Enter column name"
                              style={{ flexGrow: 1 }}
                            />
                            <IconButton size="small" onClick={() => handleRemoveColumn(tabIndex, actualColIndex)} disabled={visibleColumnCount === 1 || col.isMandatory}>
                              <DeleteOutlineIcon fontSize="small" style={{ color: (visibleColumnCount === 1 || col.isMandatory) ? 'transparent' : '#d32f2f' }} />
                            </IconButton>
                          </div>
                        </TableCell>
                        <TableCell>
                          <FormControl variant="outlined" size="small" fullWidth>
                            <Select
                              value={col.fieldType}
                              onChange={(e) => {
                                const newSections = [...allocateIngestionSections];
                                newSections[0].tabs[tabIndex].columns[actualColIndex].fieldType = e.target.value;
                                if (e.target.value === 'Chain of custody') {
                                  newSections[0].tabs[tabIndex].columns[actualColIndex].ingestion = 'Mandatory';
                                }
                                setAllocateIngestionSections(newSections);
                              }}
                              disabled={!col.columnName || !col.columnName.trim()}
                              displayEmpty
                            >
                              <MenuItem value="" disabled>
                                Select
                              </MenuItem>
                              <MenuItem value="Chain of custody" disabled={isChainOfCustodyUsed(tab, col.id)}>Chain of custody</MenuItem>
                              <MenuItem value="First Choice">First Choice</MenuItem>
                              <MenuItem value="Second Choice">Second Choice</MenuItem>
                            </Select>
                          </FormControl>
                        </TableCell>
                        <TableCell>
                          {(() => {
                              const availableOptions = getAvailableDataElements(allocateIngestionSections[0], tab, col);

                              // Find selected value in available options, or create it if not found (for loaded configs)
                              let selectedValue = null;
                              if (col.dataElementId) {
                                selectedValue = availableOptions.find((opt) => opt.dataElementId === col.dataElementId);
                                // If not found in available options but we have the data, create a temporary option
                                if (!selectedValue && col.dataElementLabel) {
                                  selectedValue = {
                                    value: col.dataElementId,
                                    label: col.dataElementLabel,
                                    dataElementKey: col.dataElement,
                                    dataElementId: col.dataElementId,
                                    disabled: false,
                                  };
                                  // Add it to available options so it can be selected
                                  availableOptions.unshift(selectedValue);
                                }
                              }

                              return (
                                <Autocomplete
                                  options={availableOptions}
                                  getOptionLabel={(option) => typeof option === 'string' ? option : option.label || ''}
                                  getOptionDisabled={(option) => option.disabled}
                                  value={selectedValue}
                                  onChange={(e, newValue) => {
                                    const newSections = [...allocateIngestionSections];
                                    if (newValue) {
                                      newSections[0].tabs[tabIndex].columns[actualColIndex].dataElementId = newValue.dataElementId;
                                      newSections[0].tabs[tabIndex].columns[actualColIndex].dataElementLabel = newValue.label;
                                      newSections[0].tabs[tabIndex].columns[actualColIndex].dataElement = newValue.dataElementKey;
                                      // Set ingestion to Mandatory for BMS Material Batch Number
                                      if (newValue.label === 'BMS Material Batch Number') {
                                        newSections[0].tabs[tabIndex].columns[actualColIndex].ingestion = 'Mandatory';
                                      }
                                    } else {
                                      newSections[0].tabs[tabIndex].columns[actualColIndex].dataElementId = null;
                                      newSections[0].tabs[tabIndex].columns[actualColIndex].dataElementLabel = '';
                                      newSections[0].tabs[tabIndex].columns[actualColIndex].dataElement = '';
                                    }
                                    setAllocateIngestionSections(newSections);
                                  }}
                                  renderInput={(params) => <TextField {...params} variant="outlined" size="small" placeholder="Search..." />}
                                  disableClearable
                                  openOnFocus
                                  disabled={!col.fieldType || col.isMandatory}
                                />
                              );
                            })()}
                        </TableCell>
                        <TableCell>
                          <FormControl variant="outlined" size="small" fullWidth>
                            <Select
                              value={col.ingestion}
                              onChange={(e) => {
                                const newSections = [...allocateIngestionSections];
                                newSections[0].tabs[tabIndex].columns[actualColIndex].ingestion = e.target.value;
                                setAllocateIngestionSections(newSections);
                              }}
                              disabled={col.fieldType === 'Chain of custody' || !col.dataElementId}
                              displayEmpty
                            >
                              <MenuItem value="Mandatory">Mandatory</MenuItem>
                              <MenuItem value="Not Mandatory">Not Mandatory</MenuItem>
                              <MenuItem value="Do not read">Do not read</MenuItem>
                            </Select>
                          </FormControl>
                        </TableCell>
                      </TableRow>
                    );
                  });
                })}
                {!isDataElementsLoading && allocateIngestionSections[0]?.tabs.map((tab, tabIndex) => {
                  if (tab.columns.length === 0) return null;
                  const displayColumns = tab.columns.filter(col => !col.markForDelete);
                  if (displayColumns.length === 0) return null;

                  return (
                    <TableRow key={`add-column-${tabIndex}`} style={{ borderBottom: 'none' }}>
                      <TableCell colSpan={2} style={{ borderBottom: 'none' }} />
                      <TableCell colSpan={4} style={{ borderBottom: 'none', padding: '8px' }}>
                        <Button
                          variant="text"
                          onClick={handleAddColumn}
                          style={{
                            color: '#ec6535',
                            textTransform: 'none',
                            // fontWeight: 600,
                            fontSize: '14px',
                            padding: '4px 0',
                            marginTop: '8px',
                          }}
                        >
                          + Add column
                        </Button>
                      </TableCell>
                      {/* <TableCell colSpan={3} style={{ borderBottom: 'none' }} /> */}
                    </TableRow>
                  );
                })}
                </TableBody>
              </Table>
            </TableContainer>

            <Box style={{ display: 'flex', justifyContent: 'center' }}>
              <Button
                variant="contained"
                className={classes.uploadBtn}
                endIcon={<ChevronRightIcon />}
                onClick={handleUpload}
                disabled={!allocateFile || isUploading || isDataElementsLoading || !isIngestionConfigComplete()}
              >
                {isUploading ? 'Uploading...' : 'Upload'}
              </Button>
            </Box>
          </Container>
        );
      case 1:
        return (
          <Container className={classes.container}>
            <Typography variant="h5" style={{ marginBottom: '1em', fontWeight: 600 }}>
              Validation Summary
            </Typography>

            {/* Summary of Records */}
            <Grid container spacing={3} justify="space-between" style={{ marginBottom: '2em' }}>
              {[
                {
                  label: 'Total Records',
                  value: validationSummary.totalRecords,
                  className: classes.totalRecordsBox,
                },
                {
                  label: 'Valid Records',
                  value: validationSummary.validRecords,
                  className: classes.validRecordsBox,
                },
                {
                  label: 'Rejected Records',
                  value: validationSummary.rejectedRecords,
                  className: classes.invalidRecordsBox,
                },
              ].map(({ label, value, className }, index) => (
                <Grid item xs={12} sm={3} key={index}>
                  <Box className={className}>
                    <Typography>{label}</Typography>
                    <Typography className={classes.number}>{value}</Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>

            {/* Rejected Records */}
            {validationSummary.rejectedRecords > 0 && validationSummary.rejectedRecordErrors.length > 0 && (
              <Grid item xs={12} style={{ marginBottom: '2em' }}>
                <Box width="100%">
                  <Accordion defaultExpanded={true}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography variant="subtitle2">Rejected record(s) error(s)</Typography>
                    </AccordionSummary>
                    <AccordionDetails
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-start',
                        textAlign: 'left',
                      }}
                    >
                      {validationSummary.rejectedRecordErrors.map((error, idx) => (
                        <Typography
                          key={idx}
                          variant="body1"
                          gutterBottom
                          display="block"
                        >
                          {error.rowNumber || `Row ${idx + 1}`} - {error.message || error.errorMessage}
                        </Typography>
                      ))}
                    </AccordionDetails>
                  </Accordion>
                </Box>
              </Grid>
            )}

            {/* Buttons */}
            <Box className={classes.buttonContainer}>
              <Button className={classes.reUploadButton} onClick={handleBack}>
                &lt; Re-upload File
              </Button>
              <Box style={{ flex: 1, display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  variant="contained"
                  className={classes.uploadButtonStyle}
                  onClick={() => setEditTableOpen(true)}
                  disabled={validationSummary.validRecords === 0}
                  endIcon={<ChevronRightIcon />}
                >
                  Upload
                </Button>
              </Box>
            </Box>
          </Container>
        );
      default:
        return <Typography>Unknown Step</Typography>;
    }
  };

  return (
    <>
      <Dialog open={open}
      // onClose={handleClose}
      fullWidth maxWidth="lg" PaperProps={{ className: classes.dialogPaper }}>
        {isUploading && (
          <Box className={classes.uploadingOverlay}>
            <CircularProgress size={40} />
            <Typography style={{ fontWeight: 600 }}>Uploading...</Typography>
          </Box>
        )}
        <DialogContent className={classes.dialogContent}>
          <Box className={classes.stepperHeader}>
            <Typography className={classes.heading}>Allocate POs</Typography>
            <Typography className={classes.subheading}>Add/update the PO allocation details through the file</Typography>
            <Box className={classes.closePageAction} onClick={handleClose}>
              <Typography style={{ fontWeight: 'bold', fontSize: '16px' }}>Cancel</Typography>
              <CloseIcon />
            </Box>
          </Box>

          <Stepper activeStep={activeStep} alternativeLabel>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {getStepContent(activeStep)}
        </DialogContent>
      </Dialog>

      {/* EditAllocationTable opens after user clicks Upload on Validation Summary step */}
      <EditAllocationTable
        open={editTableOpen}
        onClose={handleClose}
        apiResponse={{ validPOsList: validPOsFromUpload }}
        uploadedFileDetails={{
          file: allocateFile,
          fileName: allocateFile?.name || '',
          fileSize: allocateFile?.size || 0,
          fileType: allocateFileType,
        }}
        onSaveSuccess={handleSaveSuccess}
      />
    </>
  );
};

export default AllocatePOsModal;
